import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiCreateAsset, apiDeleteAsset, apiListAssets, apiPatchAsset } from '@/platform/api'
import { usePlatformStore } from '@/platform/session'
import { deleteModelBinaryCache, getModelBinary } from '@/utils/modelBinary'

export interface ModelMeta {
  /** 内容寻址 sha256（云端为唯一数据源；本地 uuid 时代的数据在首次迁移后不再使用） */
  libraryId: string
  name: string
  format: string
  originalFilename: string
  thumbnail: string  // base64 PNG
  createdAt: string
  /** 导入预览时选择的原始单位换算到场景米制（与 scene.importedModel.unitScale 一致）；旧数据缺省按 1 */
  unitScale?: number
  /** 云端 asset 登记 id（列表/删除/改名用） */
  assetPublicId?: string
  /** 二进制真实字节数（登记时随附，避免服务端记 0） */
  sizeBytes?: number
}

/**
 * 模型库（docs/10 §2.10-②）：元数据与二进制都只在云端（asset 表 + blob 内容寻址），
 * 本机 modelDB 仅作二进制缓存。libraryId = blobSha，模型二进制内容不变 → 引用永不失效。
 */
export const useModelLibraryStore = defineStore('modelLibrary', () => {
  const models = ref<ModelMeta[]>([])
  let initialized = false

  /** 从云端拉模型库清单（登录后调用；失败抛出——登录态下失败就是失败，不用本机残留） */
  async function init(): Promise<void> {
    const platform = usePlatformStore()
    if (!platform.tenant) return
    const rows = await apiListAssets(platform.tenant.publicId)
    models.value = rows.map((r) => {
      const meta = (r.meta ?? {}) as Record<string, unknown>
      return {
        libraryId: r.blobSha,
        name: r.name,
        format: r.kind === 'dxf' ? 'dxf' : (meta.format as string) || r.kind,
        originalFilename: (meta.originalFilename as string) || r.name,
        thumbnail: (meta.thumbnail as string) || '',
        createdAt: r.createdAt,
        unitScale: (meta.unitScale as number | undefined) ?? 1,
        assetPublicId: r.publicId,
      }
    })
    initialized = true
  }

  /** 本地内存列表是否已从云端加载 */
  function isInitialized(): boolean {
    return initialized
  }

  /** 登记新模型（二进制已上传为 blob；这里在云端 asset 表登记 + 本地列表更新；同 sha 幂等） */
  async function addModel(meta: ModelMeta): Promise<void> {
    const platform = usePlatformStore()
    if (!platform.tenant) return
    const created = await apiCreateAsset(platform.tenant.publicId, {
      name: meta.name,
      kind: meta.format === 'dxf' ? 'dxf' : 'model',
      blobSha: meta.libraryId,
      sizeBytes: meta.sizeBytes,
      meta: {
        format: meta.format,
        originalFilename: meta.originalFilename,
        thumbnail: meta.thumbnail,
        unitScale: meta.unitScale ?? 1,
      },
    })
    const exists = models.value.findIndex((m) => m.libraryId === meta.libraryId)
    const entry: ModelMeta = { ...meta, assetPublicId: created.publicId }
    if (exists >= 0) models.value[exists] = entry
    else models.value.unshift(entry)
  }

  async function removeModel(libraryId: string): Promise<void> {
    const model = models.value.find((m) => m.libraryId === libraryId)
    if (!model) return
    const platform = usePlatformStore()
    if (platform.tenant && model.assetPublicId) {
      await apiDeleteAsset(platform.tenant.publicId, model.assetPublicId)
    }
    models.value = models.value.filter((m) => m.libraryId !== libraryId)
    await deleteModelBinaryCache(libraryId)
  }

  async function renameModel(libraryId: string, newName: string): Promise<void> {
    const model = models.value.find((m) => m.libraryId === libraryId)
    if (!model) return
    const platform = usePlatformStore()
    if (platform.tenant && model.assetPublicId) {
      await apiPatchAsset(platform.tenant.publicId, model.assetPublicId, { name: newName })
    }
    model.name = newName
  }

  function getModel(libraryId: string): ModelMeta | undefined {
    return models.value.find((m) => m.libraryId === libraryId)
  }

  /** 取模型二进制（云端回源 + 本地缓存，见 utils/modelBinary） */
  async function getBinary(libraryId: string, name?: string): Promise<ArrayBuffer | null> {
    return getModelBinary(libraryId, name)
  }

  return { models, init, isInitialized, addModel, removeModel, renameModel, getModel, getBinary }
})
